import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Hammer, MessageSquare, Star, AlertCircle, Check, Clock3, X } from 'lucide-react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_ALL_BORROW_BOOKINGS } from '../graphql/queries/allBorrowBookings';
import { TOOL_BOOKING_THREAD } from '../graphql/queries/toolBookingThread';
import { CREATE_MESSAGE_THREAD, CREATE_MESSAGE_THREAD_INPUT } from '../graphql/mutations/createMessageThread';
import {
  CANCEL_TOOL_BOOKING,
  CANCEL_TOOL_BOOKING_DIRECT,
  PICK_UP_TOOL_BOOKING,
  PICK_UP_TOOL_BOOKING_DIRECT,
} from '../graphql/mutations/borrowToolBookingActions';
import {
  COMPLETE_TOOL_BOOKING,
  COMPLETE_TOOL_BOOKING_DIRECT,
} from '../graphql/mutations/returnToolBooking';
import {
  REVIEW_TOOL_BOOKING,
  REVIEW_TOOL_BOOKING_DIRECT,
  COMPLETE_TOOL_BOOKING_WITH_REVIEW,
  COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW,
} from '../graphql/mutations/reviewToolBooking';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const AllBorrowBookings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(true);
  const [messageThreadLoadingBookingId, setMessageThreadLoadingBookingId] = useState<string | null>(null);
  const [borrowActionDialog, setBorrowActionDialog] = useState<{ booking: any; action: 'cancel' | 'pickup' } | null>(null);
  const [borrowActionLoading, setBorrowActionLoading] = useState(false);
  const [borrowActionLoadingBookingId, setBorrowActionLoadingBookingId] = useState<string | null>(null);
  const [borrowReviewBooking, setBorrowReviewBooking] = useState<any>(null);
  const [borrowReviewRating, setBorrowReviewRating] = useState(0);
  const [borrowReviewComment, setBorrowReviewComment] = useState('');
  const [borrowReviewLoading, setBorrowReviewLoading] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const { data, loading, error, refetch: refetchBorrowBookings } = useQuery(GET_ALL_BORROW_BOOKINGS, {
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const bookings = data?.currentUser?.borrowToolBookings?.items || [];
  const totalCount = data?.currentUser?.borrowToolBookings?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const { data: threadData, loading: threadLoading } = useQuery(TOOL_BOOKING_THREAD, {
    variables: { id: selectedBooking?.id },
    skip: !selectedBooking?.id,
    fetchPolicy: 'network-only',
  });
  const [fetchBookingThread] = useLazyQuery(TOOL_BOOKING_THREAD, {
    fetchPolicy: 'network-only',
  });
  const [createMessageThread] = useMutation(CREATE_MESSAGE_THREAD);
  const [createMessageThreadInput] = useMutation(CREATE_MESSAGE_THREAD_INPUT);
  const [cancelToolBooking] = useMutation(CANCEL_TOOL_BOOKING);
  const [cancelToolBookingDirect] = useMutation(CANCEL_TOOL_BOOKING_DIRECT);
  const [pickUpToolBooking] = useMutation(PICK_UP_TOOL_BOOKING);
  const [pickUpToolBookingDirect] = useMutation(PICK_UP_TOOL_BOOKING_DIRECT);
  const [completeToolBooking] = useMutation(COMPLETE_TOOL_BOOKING);
  const [completeToolBookingDirect] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT);
  const [reviewToolBooking] = useMutation(REVIEW_TOOL_BOOKING);
  const [reviewToolBookingDirect] = useMutation(REVIEW_TOOL_BOOKING_DIRECT);
  const [completeToolBookingWithReview] = useMutation(COMPLETE_TOOL_BOOKING_WITH_REVIEW);
  const [completeToolBookingDirectWithReview] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW);

  const messages = threadData?.toolBooking?.messageThread?.messages || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatPrice = (cents: number) => {
    const kroner = cents / 100;
    const hasDecimals = cents % 100 !== 0;
    return `${kroner.toLocaleString('da-DK', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    })} kr`;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
      requested: { text: 'Anmodet', color: 'bg-yellow-100 text-yellow-800', icon: Clock3 },
      declined: { text: 'Afvist', color: 'bg-red-100 text-red-800', icon: X },
      canceled: { text: 'Annulleret', color: 'bg-gray-100 text-gray-800', icon: X },
      confirmed: { text: 'Bekræftet', color: 'bg-blue-100 text-blue-800', icon: Check },
      picked_up: { text: 'Afhentet', color: 'bg-green-100 text-green-800', icon: Check },
      returned: { text: 'Returneret', color: 'bg-purple-100 text-purple-800', icon: Check },
      reviewed_by_booking_user: { text: 'Anmeldt af lejer', color: 'bg-indigo-100 text-indigo-800', icon: Star },
      reviewed_by_owner: { text: 'Anmeldt af ejer', color: 'bg-pink-100 text-pink-800', icon: Star },
      completed: { text: 'Afsluttet', color: 'bg-green-100 text-green-800', icon: Check },
    };
    return badges[status] || { text: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
  };

  const renderStatusBadge = (status: string) => {
    const statusBadge = getStatusBadge(status);
    const StatusIcon = statusBadge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
        <StatusIcon className="h-3.5 w-3.5" />
        <span>{statusBadge.text}</span>
      </span>
    );
  };

  const getBorrowBadgeStatus = (status: string) => {
    if (status === 'reviewed_by_booking_user') return 'completed';
    if (status === 'reviewed_by_owner') return 'returned';
    return status;
  };

  const getBorrowBookingAction = (status: string) => {
    if (status === 'requested') {
      return {
        label: 'Annuller',
        variant: 'outline' as const,
        action: 'cancel' as const,
        icon: <X className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'confirmed') {
      return {
        label: 'Afhentet',
        variant: 'primary' as const,
        action: 'pickup' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'returned' || status === 'reviewed_by_owner') {
      return {
        label: 'Afslut',
        variant: 'primary' as const,
        action: 'complete' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    return null;
  };

  const getDayDifferenceFromToday = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    return Math.round((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getBorrowReminders = (booking: any) => {
    if (!booking?.fromDate || !booking?.toDate) return [];

    const hiddenStatuses = ['declined', 'canceled', 'completed', 'reviewed_by_booking_user'];
    if (hiddenStatuses.includes(booking.status)) return [];

    const reminders: Array<{ key: 'pickup' | 'return' | 'overdue'; title: string; message: string }> = [];

    const fromDayDiff = getDayDifferenceFromToday(booking.fromDate);
    const toDayDiff = getDayDifferenceFromToday(booking.toDate);

    if ((booking.status === 'requested' || booking.status === 'confirmed') && (fromDayDiff === 0 || fromDayDiff === 1)) {
      reminders.push({
        key: 'pickup',
        title: 'Afhentningspåmindelse',
        message: fromDayDiff === 0 ? 'Husk at afhente værktøjet i dag.' : 'Husk at afhente værktøjet i morgen.',
      });
    }

    if (booking.status === 'picked_up' && (toDayDiff === 0 || toDayDiff === 1)) {
      reminders.push({
        key: 'return',
        title: 'Returneringspåmindelse',
        message: toDayDiff === 0 ? 'Husk at returnere værktøjet i dag.' : 'Husk at returnere værktøjet i morgen.',
      });
    }

    if (booking.status === 'picked_up' && toDayDiff < 0) {
      reminders.push({
        key: 'overdue',
        title: 'Returnering overskredet',
        message: 'Returneringsdatoen er overskredet. Husk at returnere værktøjet hurtigst muligt.',
      });
    }

    return reminders;
  };

  const getBookingCounterparty = (booking: any) => {
    if (!booking) return null;
    if (booking?.bookingUser?.id === user?.id) return booking?.owningUser || null;
    if (booking?.owningUser?.id === user?.id) return booking?.bookingUser || null;
    return booking?.owningUser || booking?.bookingUser || null;
  };

  const handleOpenBookingDialog = (booking: any) => {
    setIsMessagesCollapsed(true);
    setSelectedBooking(booking);
  };

  const isSchemaArgumentError = (mutationError: any) => {
    const graphQLErrors = mutationError?.graphQLErrors || [];
    return graphQLErrors.some((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      return code === 'missingRequiredArguments' || code === 'argumentNotAccepted' || code === 'variableNotUsed';
    });
  };

  const handleOpenBookingConversation = async (booking: any) => {
    if (!booking?.id) return;

    setMessageThreadLoadingBookingId(booking.id);

    try {
      if (booking?.messageThread?.id) {
        navigate(`/conversation/${booking.messageThread.id}`);
        return;
      }

      const { data: threadLookupData } = await fetchBookingThread({
        variables: { id: booking.id },
      });

      const existingThreadId = threadLookupData?.toolBooking?.messageThread?.id;
      if (existingThreadId) {
        navigate(`/conversation/${existingThreadId}`);
        return;
      }

      const toolId = booking?.tool?.id;
      if (!toolId) {
        toast.error('Kunne ikke finde værktøjet for beskeden.');
        return;
      }

      const counterparty = getBookingCounterparty(booking);
      const variables = {
        toolId,
        toolBookingId: booking.id,
        participantIds: counterparty?.id ? [counterparty.id] : undefined,
      };

      let createThreadData: any;
      try {
        const response = await createMessageThread({ variables });
        createThreadData = response.data;
      } catch (createThreadError) {
        if (!isSchemaArgumentError(createThreadError)) {
          throw createThreadError;
        }

        const fallbackResponse = await createMessageThreadInput({ variables });
        createThreadData = fallbackResponse.data;
      }

      const createThreadErrors = createThreadData?.createMessageThread?.errors || [];
      if (createThreadErrors.length > 0) {
        toast.error(createThreadErrors[0]);
        return;
      }

      const createdThreadId = createThreadData?.createMessageThread?.messageThread?.id;
      if (!createdThreadId) {
        toast.error('Kunne ikke oprette en ny beskedtråd.');
        return;
      }

      navigate(`/conversation/${createdThreadId}`);
    } catch {
      toast.error('Der opstod en fejl ved åbning af beskedtråden.');
    } finally {
      setMessageThreadLoadingBookingId(null);
    }
  };

  const handleOpenBorrowActionDialog = (booking: any, action: 'cancel' | 'pickup') => {
    setBorrowActionDialog({ booking, action });
  };

  const handleCloseBorrowActionDialog = () => {
    if (borrowActionLoading) return;
    setBorrowActionDialog(null);
  };

  const resetBorrowReviewDialogState = () => {
    setBorrowReviewBooking(null);
    setBorrowReviewRating(0);
    setBorrowReviewComment('');
  };

  const handleOpenBorrowReviewDialog = (booking: any) => {
    setBorrowReviewBooking(booking);
    setBorrowReviewRating(0);
    setBorrowReviewComment('');
  };

  const handleCloseBorrowReviewDialog = () => {
    if (borrowReviewLoading) return;
    resetBorrowReviewDialogState();
  };

  const shouldTryNextBorrowActionMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const handleConfirmBorrowAction = async (message: string) => {
    const booking = borrowActionDialog?.booking;
    const action = borrowActionDialog?.action;
    if (!booking?.id || !action) return;

    setBorrowActionLoading(true);
    setBorrowActionLoadingBookingId(booking.id);

    const variables = {
      id: booking.id,
      message: message?.trim() || null,
    };

    try {
      const getCancelPayload = (data: any) => data?.cancelToolBooking;
      const getPickupPayload = (data: any) => data?.pickUpToolBooking;

      const attempts =
        action === 'cancel'
          ? [
              () => cancelToolBooking({ variables }),
              () => cancelToolBookingDirect({ variables }),
            ]
          : [
              () => pickUpToolBooking({ variables }),
              () => pickUpToolBookingDirect({ variables }),
            ];

      const getPayload = action === 'cancel' ? getCancelPayload : getPickupPayload;

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextBorrowActionMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel mutation for handlingen.');
      }

      const actionErrors = payload?.errors || [];
      if (actionErrors.length > 0) {
        toast.error(actionErrors[0]);
        return;
      }

      toast.success(action === 'cancel' ? 'Låneanmodningen er annulleret.' : 'Lånet er markeret som afhentet.');
      setBorrowActionDialog(null);
      setSelectedBooking(null);
      await refetchBorrowBookings();
    } catch {
      toast.error(action === 'cancel' ? 'Der opstod en fejl ved annullering af lånet.' : 'Der opstod en fejl ved markering af afhentning.');
    } finally {
      setBorrowActionLoading(false);
      setBorrowActionLoadingBookingId(null);
    }
  };

  const handleCompleteBorrowWithoutReview = async () => {
    if (!borrowReviewBooking?.id) return;

    setBorrowReviewLoading(true);

    const note = borrowReviewComment.trim();
    const message = note
      ? `Lån afsluttet uden bedømmelse. ${note}`
      : 'Lån afsluttet uden bedømmelse.';
    const variables = {
      id: borrowReviewBooking.id,
      message,
    };

    try {
      const attempts = [
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = response?.data?.completeToolBooking;
          if (payload) break;
        } catch (error) {
          if (shouldTryNextBorrowActionMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel afslutnings-mutation.');
      }

      const completeErrors = payload?.errors || [];
      if (completeErrors.length > 0) {
        toast.error(completeErrors[0]);
        return;
      }

      toast.success('Lånet er afsluttet.');
      resetBorrowReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afslutning af lånet.');
    } finally {
      setBorrowReviewLoading(false);
    }
  };

  const handleSubmitBorrowReview = async () => {
    if (!borrowReviewBooking?.id) return;
    if (borrowReviewRating < 1) {
      await handleCompleteBorrowWithoutReview();
      return;
    }

    setBorrowReviewLoading(true);

    const comment = borrowReviewComment.trim() || null;
    const reviewVariables = {
      id: borrowReviewBooking.id,
      rating: borrowReviewRating,
      comment,
    };
    const reviewAndCompleteVariables = {
      id: borrowReviewBooking.id,
      rating: borrowReviewRating,
      comment,
      message: 'Lån afsluttet med bedømmelse.',
    };
    const completeVariables = {
      id: borrowReviewBooking.id,
      message: comment
        ? `Lån afsluttet med bedømmelse. ${comment}`
        : 'Lån afsluttet med bedømmelse.',
    };

    const getPayload = (data: any) => data?.reviewToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => reviewToolBooking({ variables: reviewVariables }),
        () => reviewToolBookingDirect({ variables: reviewVariables }),
        () => completeToolBookingWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBookingDirectWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBooking({ variables: completeVariables }),
        () => completeToolBookingDirect({ variables: completeVariables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextBorrowActionMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel bedømmelses-mutation.');
      }

      const reviewErrors = payload?.errors || [];
      if (reviewErrors.length > 0) {
        toast.error(reviewErrors[0]);
        return;
      }

      toast.success('Bedømmelse gemt og lånet er afsluttet.');
      resetBorrowReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afsendelse af bedømmelsen.');
    } finally {
      setBorrowReviewLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Tilbage til dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Alle lån</h1>
          <p className="mt-2 text-gray-600">Oversigt over alle dine lån</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Indlæser...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">Der opstod en fejl ved indlæsning af lån</p>
            </div>
          ) : bookings.length > 0 ? (
            <>
              <div className="space-y-4">
                {bookings.map((booking: any) => {
                  const actionButton = getBorrowBookingAction(booking.status);
                  const owner = booking.owningUser;
                  const reminders = getBorrowReminders(booking);

                  return (
                    <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
                      <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                        {booking.tool.primaryImageUrl ? (
                          <img src={booking.tool.primaryImageUrl} alt={booking.tool.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-gray-100 flex items-center justify-center text-gray-400">
                            <Hammer className="h-8 w-8" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                {booking.tool.name}
                              </Link>
                              {booking.totalPriceCents && (
                                <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                  {formatPrice(booking.totalPriceCents)}
                                </div>
                              )}
                            </div>

                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                              </span>
                              {renderStatusBadge(getBorrowBadgeStatus(booking.status))}
                            </div>

                            <div className="mt-2 flex items-start gap-2">
                              <Link
                                to={`/profile/${owner?.id}`}
                                className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                              >
                                {owner?.avatarImageUrl ? (
                                  <img
                                    src={owner.avatarImageUrl}
                                    alt={owner.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span>{owner?.name?.charAt(0).toUpperCase() || 'U'}</span>
                                )}
                              </Link>
                              <div className="text-sm leading-tight">
                                <p className="text-gray-500">
                                  Ejet af:{' '}
                                  <Link to={`/profile/${owner?.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                    {owner?.name || 'Ukendt bruger'}
                                  </Link>
                                </p>
                                <Link
                                  to={`/profile/${owner?.id}?tab=reviews`}
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                >
                                  <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                  <span>
                                    {owner?.ratingAverage !== null && owner?.reviewCount > 0
                                      ? owner.ratingAverage.toFixed(1)
                                      : 'Ingen anmeldelser'}
                                  </span>
                                </Link>
                              </div>
                            </div>

                            {reminders.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {reminders.map((reminder) => (
                                  <div
                                    key={reminder.key}
                                    className={`rounded-md border px-3 py-2 ${
                                      reminder.key === 'pickup'
                                        ? 'bg-blue-50 border-blue-200 text-blue-800'
                                        : reminder.key === 'return'
                                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                                          : 'bg-red-50 border-red-200 text-red-800'
                                    }`}
                                  >
                                    <p className="inline-flex items-center gap-1 text-sm font-medium">
                                      <Calendar className="h-4 w-4" />
                                      {reminder.title}
                                    </p>
                                    <p className="mt-0.5 text-xs">{reminder.message}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                            {booking.totalPriceCents && (
                              <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                {formatPrice(booking.totalPriceCents)}
                              </div>
                            )}

                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              icon={<MessageSquare className="h-4 w-4 mr-2" />}
                              isLoading={messageThreadLoadingBookingId === booking.id}
                              onClick={() => handleOpenBookingConversation(booking)}
                            >
                              Send besked
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              onClick={() => handleOpenBookingDialog(booking)}
                            >
                              Detaljer
                            </Button>

                            {actionButton && (
                              <Button
                                variant={actionButton.variant}
                                size="sm"
                                fullWidth
                                icon={actionButton.icon}
                                isLoading={borrowActionLoadingBookingId === booking.id}
                                onClick={() => {
                                  if (actionButton.action === 'cancel') {
                                    handleOpenBorrowActionDialog(booking, 'cancel');
                                    return;
                                  }
                                  if (actionButton.action === 'pickup') {
                                    handleOpenBorrowActionDialog(booking, 'pickup');
                                    return;
                                  }
                                  if (actionButton.action === 'complete') {
                                    handleOpenBorrowReviewDialog(booking);
                                    return;
                                  }
                                }}
                              >
                                {actionButton.label}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Viser {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} af {totalCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Side {currentPage} af {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Ingen lån fundet</p>
            </div>
          )}
        </div>
      </div>

      {selectedBooking && (
        <Dialog
          isOpen={!!selectedBooking}
          onClose={() => setSelectedBooking(null)}
          title="Booking detaljer"
          size="lg"
        >
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Værktøj</h3>
              <div className="flex gap-4">
                {selectedBooking.tool.primaryImageUrl ? (
                  <img
                    src={selectedBooking.tool.primaryImageUrl}
                    alt={selectedBooking.tool.name}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                    <Hammer className="h-8 w-8" />
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-gray-900">{selectedBooking.tool.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{selectedBooking.tool.description}</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Lejeperiode</h3>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
                <p className="text-gray-900">
                  {formatDate(selectedBooking.fromDate)} - {formatDate(selectedBooking.toDate)}
                </p>
                {renderStatusBadge(getBorrowBadgeStatus(selectedBooking.status))}
              </div>
            </div>

            {selectedBooking.totalPriceCents && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Pris</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {formatPrice(selectedBooking.totalPriceCents)}
                </p>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ejer information</h3>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium text-lg overflow-hidden">
                  {selectedBooking.owningUser?.avatarImageUrl ? (
                    <img
                      src={selectedBooking.owningUser.avatarImageUrl}
                      alt={selectedBooking.owningUser.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span>{selectedBooking.owningUser?.name?.charAt(0).toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedBooking.owningUser?.name || 'Ukendt bruger'}</p>
                  {selectedBooking.owningUser?.reviewCount > 0 ? (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span>{selectedBooking.owningUser.ratingAverage?.toFixed(1)}</span>
                      <span className="text-gray-400">({selectedBooking.owningUser.reviewCount} anmeldelser)</span>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Ingen anmeldelser endnu</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 mb-3 text-left text-gray-700 hover:text-gray-900"
                onClick={() => setIsMessagesCollapsed((collapsed) => !collapsed)}
                aria-label={isMessagesCollapsed ? 'Expandér beskeder' : 'Kollaps beskeder'}
              >
                <h3 className="text-sm font-medium">Beskeder</h3>
                {isMessagesCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>

              {!isMessagesCollapsed && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
                  {threadLoading ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Indlæser beskeder...</p>
                    </div>
                  ) : messages.length > 0 ? (
                    messages.map((message: any) => {
                      const isOwner = message.sender.id === user?.id;
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isOwner ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-xs px-4 py-2 rounded-lg ${
                            isOwner
                              ? 'bg-teal-600 text-white'
                              : 'bg-white text-gray-900 border border-gray-200'
                          }`}>
                            <p className="text-sm">{message.content}</p>
                            <p className={`text-xs mt-1 ${
                              isOwner ? 'text-teal-100' : 'text-gray-400'
                            }`}>
                              {formatDate(message.sentAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Ingen beskeder endnu</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setSelectedBooking(null)}>
                Luk
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <Dialog
        isOpen={!!borrowReviewBooking}
        onClose={handleCloseBorrowReviewDialog}
        title="Afslut lån"
        size="md"
      >
        {borrowReviewBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Giv evt. en bedømmelse af udlejeren og afslut lånet.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{borrowReviewBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(borrowReviewBooking.fromDate)} - {formatDate(borrowReviewBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Antal stjerner (valgfri)</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setBorrowReviewRating(value)}
                    className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={borrowReviewLoading}
                    aria-label={`${value} stjerner`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        value <= borrowReviewRating ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {borrowReviewRating > 0 ? `${borrowReviewRating} ud af 5 stjerner valgt` : 'Ingen stjerner valgt endnu'}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kommentar (valgfri)</label>
              <textarea
                value={borrowReviewComment}
                onChange={(event) => setBorrowReviewComment(event.target.value)}
                placeholder="Skriv en kommentar til udlejeren..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={borrowReviewLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseBorrowReviewDialog}
                disabled={borrowReviewLoading}
              >
                Annuller
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitBorrowReview}
                isLoading={borrowReviewLoading}
                disabled={borrowReviewLoading}
              >
                Afslut
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={!!borrowActionDialog}
        onClose={handleCloseBorrowActionDialog}
        onConfirm={handleConfirmBorrowAction}
        title={borrowActionDialog?.action === 'cancel' ? 'Annuller lån' : 'Bekræft afhentning'}
        description={
          borrowActionDialog?.action === 'cancel'
            ? 'Er du sikker på, at du vil annullere denne låneanmodning?'
            : 'Bekræft at værktøjet er afhentet.\n\nHar du tjekket, at alt tilbehør er med, og at værktøjets tilstand er som beskrevet?'
        }
        confirmLabel={borrowActionDialog?.action === 'cancel' ? 'Annuller lån' : 'Marker som afhentet'}
        confirmButtonVariant="primary"
        showMessageInput={borrowActionDialog?.action === 'cancel'}
        messagePlaceholder="Skriv en kommentar (valgfri)..."
        isLoading={borrowActionLoading}
      />
    </div>
  );
};

export default AllBorrowBookings;
